import React, { useState, useEffect, useCallback } from 'react';
import { Button, Box } from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import TopLabelTextField from '../../../components/TopLabelTextField/TopLabelTextField';
import CustomDatePicker from '../../../components/DatePicker/CustomDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  StudentPaymentSchemeWithMetrics,
  PaymentStatus
} from '../../../types/paymentScheme';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { SnackbarSeverityType } from '../../../enums';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { superAdminUsers } from '../../../constants/superAdminUsers';

interface UpdateFeeStatusDialogProps {
  open: boolean;
  onClose: () => void;
  scheme: StudentPaymentSchemeWithMetrics | null;
  onUpdate: () => void;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const UpdateFeeStatusDialog: React.FC<UpdateFeeStatusDialogProps> = ({
  open,
  onClose,
  scheme,
  onUpdate,
  onShowToast
}) => {
  const { paymentUser } = useSelector((state: RootState) => state.paymentUser);
  const isSuperAdmin = superAdminUsers.includes(paymentUser?.email);

  const [particulars, setParticulars] = useState('');
  const [feePeriod, setFeePeriod] = useState('');
  const [amountPayable, setAmountPayable] = useState<number | null>(null);
  const [paymentDueDate, setPaymentDueDate] = useState<Dayjs | null>(null);
  const [description, setDescription] = useState('');
  const [remarks, setRemarks] = useState('');
  const [status, setStatus] = useState<PaymentStatus | ''>('');
  const [originalStatus, setOriginalStatus] = useState<PaymentStatus | ''>('');
  const [initialStatusChangeDate, setInitialStatusChangeDate] =
    useState<Dayjs | null>(null);
  const [userSelectedStatusChangeDate, setUserSelectedStatusChangeDate] =
    useState<Dayjs | null>(null);

  const resetState = useCallback(() => {
    setParticulars('');
    setFeePeriod('');
    setAmountPayable(null);
    setPaymentDueDate(null);
    setDescription('');
    setRemarks('');
    setStatus('');
    setOriginalStatus('');
    setInitialStatusChangeDate(null);
    setUserSelectedStatusChangeDate(null);
  }, []);

  const getLatestStatusChangeDate = useCallback(
    (currentScheme: StudentPaymentSchemeWithMetrics | null) => {
      if (
        currentScheme?.paymentSchemeStatusLogs &&
        currentScheme.paymentSchemeStatusLogs.length > 0
      ) {
        const sortedLogs = currentScheme.paymentSchemeStatusLogs.sort(
          (a, b) =>
            new Date(b.statusChangeDate || '').getTime() -
            new Date(a.statusChangeDate || '').getTime()
        );
        return dayjs(sortedLogs[0].statusChangeDate);
      }
      return null;
    },
    []
  );

  useEffect(() => {
    if (scheme) {
      setParticulars(scheme.particulars);
      setFeePeriod(scheme.feePeriod);
      setAmountPayable(scheme.amount || null);
      setPaymentDueDate(
        scheme.paymentDueDate
          ? dayjs(scheme.paymentDueDate, 'Do MMM YYYY')
          : null
      );
      setDescription(scheme.description || '');
      setRemarks(scheme.remark || '');
      setStatus(scheme.paymentStatus || '');
      setOriginalStatus(scheme.paymentStatus || '');

      const latestLogDate = getLatestStatusChangeDate(scheme);
      setInitialStatusChangeDate(latestLogDate);
      setUserSelectedStatusChangeDate(latestLogDate);
    }
  }, [scheme, getLatestStatusChangeDate, open, resetState]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const isFeeStatusChanged = status !== originalStatus;

  const handleUpdate = async () => {
    if (!scheme?._id || !status) {
      onShowToast(
        'Scheme ID or status is missing.',
        SnackbarSeverityType.error
      );
      return;
    }

    try {
      const amountToBeUpdated =
        amountPayable === null ? 0 : parseFloat(amountPayable.toString());

      const payload = {
        paymentStatus: status,
        remark: remarks,
        statusChangeDate: isFeeStatusChanged
          ? userSelectedStatusChangeDate?.format('YYYY-MM-DD') || ''
          : initialStatusChangeDate?.format('YYYY-MM-DD') || '',
        amount: isSuperAdmin ? amountToBeUpdated : undefined
      };

      await paymentSchemesApis.updatePaymentSchemeStatus(scheme._id, payload);
      onShowToast(
        'Fee status updated successfully!',
        SnackbarSeverityType.success
      );
      onUpdate();
      onClose();
      resetState();
    } catch (error: any) {
      console.error('Error updating fee status:', error);
      onShowToast(
        error?.errors?.[0]?.message || 'Failed to update fee status.',
        SnackbarSeverityType.error
      );
    }
  };

  const handleStatusChange = (e: any) => {
    const newStatus = e.target.value as PaymentStatus;
    setStatus(newStatus);
    if (newStatus !== originalStatus) {
      setUserSelectedStatusChangeDate(dayjs());
    } else {
      setUserSelectedStatusChangeDate(initialStatusChangeDate);
    }
  };

  const statusOptions: DropdownSelectOption[] = Object.values(
    PaymentStatus
  ).map((s) => ({
    value: s,
    label: s
  }));

  const dialogActions = (
    <Button
      variant='contained'
      onClick={handleUpdate}
      sx={{ textTransform: 'none' }}
      disabled={!status}
    >
      Save
    </Button>
  );

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Update Fee Status'
      maxWidth='sm'
      actions={dialogActions}
    >
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Particulars'
          placeholder='Enter Particulars'
          value={particulars}
          fullWidth
          hasTopLabel
          required
          disabled
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TopLabelTextField
            label='Fee Period'
            placeholder='Enter Fee Period'
            value={feePeriod}
            fullWidth
            hasTopLabel
            required
            disabled
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <TopLabelTextField
            label='Amount Payable'
            placeholder='Enter Amount Payable'
            type='number'
            value={amountPayable === null ? '' : amountPayable}
            onChange={(e) =>
              setAmountPayable(
                e.target.value === '' ? null : Number(e.target.value)
              )
            }
            fullWidth
            hasTopLabel
            disabled={!isSuperAdmin}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='Payment Due Date'
              onChange={(newValue: Dayjs | null) => setPaymentDueDate(newValue)}
              value={paymentDueDate}
              required
              disabled
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ flex: 1 }}>
          <TopLabelTextField
            label='Description'
            placeholder='Enter Description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            hasTopLabel
            disabled
          />
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <TopLabelTextField
          label='Remarks'
          placeholder='Enter Remarks'
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          fullWidth
          rows={3}
          hasTopLabel
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <DropdownSelect
          label='Status'
          placeholder='Select Status'
          value={status}
          options={statusOptions}
          onChange={handleStatusChange}
          name='status'
          required
          fullWidth
          hasTopLabel
        />
      </Box>
      {isFeeStatusChanged && (
        <Box sx={{ mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <CustomDatePicker
              label='Status Change Date'
              onChange={(newValue: Dayjs | null) =>
                setUserSelectedStatusChangeDate(newValue)
              }
              value={userSelectedStatusChangeDate}
            />
          </LocalizationProvider>
        </Box>
      )}
    </DialogModal>
  );
};

export default UpdateFeeStatusDialog;
